// 每日經期伴隨紀錄（Daily Log，W58.1 · W58.5 情境化）。
//
// **為什麼它是一個面板而不是四個欄位。** 流量、痛經、症狀標籤是**同一個人
// 在同一天的狀況** —— 分成三個獨立入口的話，使用者要開三次面板、
// 存三次。所以它們在同一張卡裡，一次存。
//
// **點兩下就能記完**是這個面板的設計目標（Meetyou 的「今天怎麼樣」）。
// 所以：流量與痛經是**單選**（一組 pill）、症狀是**可複選**的標籤、
// 備註是選填。
//
// **W58.5 —— 這張表單變成「依情境」。**
//
// 使用者要求：
//
// > **「僅在『實際經期』期間預設顯示『流量（點滴/少/中/多）』與『痛經』；
// > 非經期階段（濾泡期、排卵期、黃體期）預設只顯示『症狀與心情』及
// > 『🌸 標記今日經期開始』快捷按鈕。」**
// >
// **「若當日尚未填寫經期紀錄，請將下方表單預設收折為一張輕量卡片，
// > 避免干擾雙人查看當日主要行程（如旅行、休假）。」**
//
// **為什麼在安全期不問流量。** 在安全期問「今天流量多少」是問了一個**不存在的
// 東西** —— 而一張表單裡多兩個不相干的欄位，使用者要略過它們才知道那些欄位
// 現在不適用。**沒有記錄的那一天，預設是空的。**
//
// **「實際經期」是事實，不是推算。** 它來自後端的 `cycle_state.actual`
// （ADR-0211）—— 有一筆記錄的經期事件覆蓋今天。前端**不**從推算去猜。

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plane } from "lucide-react";

import {
  CRAMP_LABEL,
  CRAMP_OPTIONS,
  FLOW_LABEL,
  FLOW_OPTIONS,
  SYMPTOM_OPTIONS,
  deleteCycleLog,
  upsertCycleLog,
  type CrampValue,
  type CycleLog,
  type FlowValue,
  type OwnerToken,
} from "@/lib/api/calendar";
import { ApiError } from "@/lib/api/pair";
import { CycleFlower } from "@/components/calendar/CycleFlower";
import { queryKeys } from "@/lib/queries/keys";
import { cn } from "@/lib/utils/cn";

export interface CycleLogPanelProps {
  /** 被記錄的那一天（`YYYY-MM-DD`）。 */
  date: string;
  /** 這一天已有的紀錄（沒有就是 `null`）。 */
  log: CycleLog | null;
  /**
   * **這一對的週期是誰的身體**（相對於呼叫者）—— 由後端判定。
   *
   * **W58.4 —— 這裡曾經有一個「我 / TA」的切換器，而它是錯的。**
   *
   * 使用者的反饋：「女方自己記，男方也可幫女方記，但現時選擇我/TA 是有問題的」。
   * 正確的模型是**歸屬固定、記錄者自由**：經期是她的身體，兩個人都能幫她記。
   * 讓前端選歸屬會造成一個具體的資料錯誤 —— 男方按「我」會記成自己的身體，
   * **遮住**女方同一天的真實紀錄。
   *
   * 所以現在這個值只是**顯示用的**：面板要說清楚「這是誰的身體」。
   */
  owner: OwnerToken;
  /**
   * W58.5 — **當天是不是「實際經期日」。**
   *
   * 事實（有一筆記錄的經期事件覆蓋今天），不是推算。來自後端的
   * `cycle_state.actual`（ADR-0211）。
   */
  inPeriod: boolean;
  /**
   * W58.5 — **當天有共同行程 / 旅行，而且處在經期 / 排卵期 / 經期前夕。**
   *
   * 條件在 `DaySheet` 裡判斷（它才有事件清單與階段），這裡只負責呈現。
   */
  tripHint?: boolean;
  /**
   * W58.5 — 「標記今日經期開始」。開一個今天是週期類型的表單。
   */
  onStartPeriod?: () => void;
  /** 存好之後叫一聲，讓上層刷新月曆。 */
  onSaved?: () => void;
}

export function CycleLogPanel({
  date,
  log,
  owner,
  inPeriod,
  tripHint = false,
  onStartPeriod,
  onSaved,
}: CycleLogPanelProps) {
  const queryClient = useQueryClient();
  const [flow, setFlow] = useState<FlowValue | null>(asFlow(log?.flow));
  const [cramps, setCramps] = useState<CrampValue | null>(asCramp(log?.cramps));
  const [symptoms, setSymptoms] = useState<string[]>(log?.symptoms ?? []);
  const [note, setNote] = useState(log?.note ?? "");
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // W58.5 — **還沒記錄過的日子，表單預設收折。**
  //
  // 日詳情的主體是**事件**（旅行、休假）。一個四段的空白表單壓在「今天有什麼」
  // 下面，是在用表單的體積蓋掉列表的答案（ADR-0208 剛剛才把週期狀態移到
  // 當日安排**下面**，理由是同一個）。
  //
  // **已經有紀錄的日子直接展開** —— 那是「顯示資料」，不是「填表單」。
  const [expanded, setExpanded] = useState(log != null);

  // 換一天就重新讀那一格的狀態。
  //
  // **W58.4 —— 依賴裡沒有 `owner` 了。** 以前 owner 是一個可切的 state，所以
  // 換 owner 要重讀；現在歸屬由後端決定，換 day 就夠了。
  useEffect(() => {
    setFlow(asFlow(log?.flow));
    setCramps(asCramp(log?.cramps));
    setSymptoms(log?.symptoms ?? []);
    setNote(log?.note ?? "");
    setError(null);
    setSavedAt(null);
    setExpanded(log != null);
  }, [date, log]);

  const save = useMutation({
    mutationFn: () =>
      // **W58.4 —— 不送 `owner_id`。** 歸屬是後端判定的事；送了會被 422。
      upsertCycleLog(date, {
        flow,
        cramps,
        symptoms,
        note: note.trim() ? note.trim() : null,
      }),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      void queryClient.invalidateQueries({ queryKey: ["calendar-day"] });
      void queryClient.invalidateQueries({ queryKey: ["calendar-month"] });
      void queryClient.invalidateQueries({ queryKey: ["calendar-cycle-logs"] });
      // W51 D-1 — 任何寫入都要讓首頁失效（`useDashboard` 有 30 秒 staleTime）。
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      onSaved?.();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "儲存失敗，請稍候再試");
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteCycleLog(date),
    onSuccess: () => {
      setFlow(null);
      setCramps(null);
      setSymptoms([]);
      setNote("");
      setError(null);
      setSavedAt(Date.now());
      void queryClient.invalidateQueries({ queryKey: ["calendar-day"] });
      void queryClient.invalidateQueries({ queryKey: ["calendar-month"] });
      void queryClient.invalidateQueries({ queryKey: ["calendar-cycle-logs"] });
      // W51 D-1 — 任何寫入都要讓首頁失效（`useDashboard` 有 30 秒 staleTime）。
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      onSaved?.();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "刪除失敗，請稍候再試");
    },
  });

  function toggleSymptom(tag: string) {
    setSymptoms((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  }

  // W58.5 — **流量與痛經只在「實際經期」時預設出現。**
  //
  // **「已經記過流量 / 痛經」也算經期** —— 一筆記錄存在，就代表那一天**曾經**
  // 是經期，把它藏起來是資料的消失。
  const showPeriodFields = inPeriod || flow != null || cramps != null;

  return (
    <section
      className="rounded-md border border-hairline bg-paper-deep p-s-3"
      data-testid="calendar-cycle-log"
    >
      <div className="flex items-start justify-between gap-s-2">
        <div>
          <h3 className="text-micro uppercase tracking-wide text-ink-3">每日紀錄</h3>
          {/*
            **W58.4 —— 這裡曾經是一個「我 / TA」的切換器，而它是錯的。**

            「女方自己記，男方也可幫女方記」：經期是**她的**身體，兩個人都能幫她
            記。所以面板不再問「你要記誰的」，而是**講清楚這是誰的** ——
            「我的身體狀況」或「替 TA 記錄」。

            而「誰按了儲存」是另一件事（Q19 的稽核透明性）：同一筆資料在兩個人
            面前講不同的話 —— 女方看到「由 TA 記錄」，男方看到「由 你 記錄」。
          */}
          <p className="mt-s-1 text-meta text-ink-2" data-testid="calendar-cycle-log-owner">
            {owner === "me" ? "我的身體狀況" : "替 TA 記錄"}
          </p>
        </div>
        {log ? (
          <span className="shrink-0 text-meta text-ink-3" data-testid="calendar-cycle-log-recorder">
            由 {log.recorded_by_name} 記錄
          </span>
        ) : null}
      </div>

      {/*
        W58.5 — **雙人行程聯動貼士。**

        條件（由 `DaySheet` 判斷）：當日有共同行程 / 旅行，**且**當天處於
        經期 / 排卵期 / 經期前夕。

        **圖示不是 emoji**（root `AGENTS.md §3 #19`）—— 用行程本來就有的
        `Plane`。
      */}
      {tripHint ? (
        <p
          className="mt-s-3 flex items-start gap-s-2 rounded-sm bg-shared-tint px-s-3 py-s-2 text-meta text-shared-ink"
          data-testid="calendar-cycle-trip-hint"
        >
          <Plane size={14} aria-hidden="true" className="mt-s-0.5 shrink-0" />
          <span>今日有共同行程，注意保暖與適度休息喔！</span>
        </p>
      ) : null}

      {expanded ? (
        <div className="mt-s-3 flex flex-col gap-s-3">
          {showPeriodFields ? (
            <>
              <ChoiceRow
                label="流量"
                options={FLOW_OPTIONS.map((v) => ({ value: v, label: FLOW_LABEL[v] }))}
                value={flow}
                onChange={(v) => setFlow(v as FlowValue | null)}
                testIdPrefix="calendar-cycle-flow"
                petals={FLOW_OPTIONS.length}
              />
              <ChoiceRow
                label="痛經"
                options={CRAMP_OPTIONS.map((v) => ({ value: v, label: CRAMP_LABEL[v] }))}
                value={cramps}
                onChange={(v) => setCramps(v as CrampValue | null)}
                testIdPrefix="calendar-cycle-cramps"
              />
            </>
          ) : (
            // W58.5 — **非經期時的快捷動作。**
            // 沒有它，使用者在安全期想「其實我今天開始了」就只能去月曆上
            // 長按建立一個事件 —— 一條比「按一下」長得多的路。
            onStartPeriod ? (
              <button
                type="button"
                onClick={onStartPeriod}
                data-testid="calendar-cycle-start-period"
                className="tap-target flex items-center justify-center gap-s-2 self-start rounded-pill border border-hairline-strong px-s-4 py-s-2 text-body-sm text-ink-1 hover:bg-black/5"
              >
                <FlowerGlyph />
                標記今日經期開始
              </button>
            ) : null
          )}

          <div className="flex flex-col gap-s-1.5">
            <span className="font-label text-ink-2">症狀與心情</span>
            <div className="flex flex-wrap gap-s-1">
              {SYMPTOM_OPTIONS.map((tag) => {
                const on = symptoms.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleSymptom(tag)}
                    data-testid={`calendar-cycle-symptom-${tag}`}
                    className={cn(
                      "min-h-11 rounded-pill border px-s-2 text-meta",
                      on
                        ? "border-you-strong bg-you-tint text-you-strong"
                        : "border-hairline-strong text-ink-2 hover:bg-black/5",
                    )}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-s-1.5">
            <label htmlFor="calendar-cycle-note" className="font-label text-ink-2">
              備註
            </label>
            <input
              id="calendar-cycle-note"
              type="text"
              maxLength={200}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例如：今天特別累"
              data-testid="calendar-cycle-note"
              className="h-12 rounded-md border border-hairline bg-paper px-3 text-body-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
            />
          </div>
        </div>
      ) : (
        // W58.5 — **收折狀態：一張輕量卡片。**
        //
        // 只有標題、一句話與一個展開鈕。它不佔空間、不搶視線，而且**說清楚
        // 這一格是什麼**（還沒有記錄）。
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="calendar-cycle-log-expand"
          className="tap-target mt-s-3 flex w-full items-center justify-between gap-s-2 rounded-sm border border-hairline px-s-3 py-s-2 text-left text-meta text-ink-2 hover:bg-black/5"
        >
          <span>還沒有記錄今天的身體狀況</span>
          <span className="shrink-0 font-semibold text-you-strong">記錄今天</span>
        </button>
      )}

      {error ? (
        <p className="mt-s-2 text-meta text-partner-strong" role="alert" data-testid="calendar-cycle-log-error">
          {error}
        </p>
      ) : null}
      {savedAt ? (
        <p className="mt-s-2 text-meta text-ink-3" data-testid="calendar-cycle-log-saved">
          已儲存
        </p>
      ) : null}

      {expanded ? (
        <div className="mt-s-3 flex items-center gap-s-2">
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            data-testid="calendar-cycle-log-save"
            className="min-h-11 rounded-pill bg-ink-1 px-s-4 text-body-sm text-paper disabled:opacity-50"
          >
            {save.isPending ? "儲存中…" : "儲存"}
          </button>
          {log ? (
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              data-testid="calendar-cycle-log-delete"
              className="min-h-11 px-s-2 text-meta text-ink-2 hover:underline disabled:opacity-50"
            >
              清除這天
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** The flower glyph for 「標記今日經期開始」—— 與全站同一朵花。 */
function FlowerGlyph() {
  return (
    <span
      className="flex h-[var(--chip-h)] w-[var(--chip-h)] shrink-0 items-center justify-center rounded-[6px] bg-partner-tint text-partner-strong"
      aria-hidden="true"
    >
      <CycleFlower size={13} />
    </span>
  );
}

/**
 * 契約上的 `flow` / `cramps` 是裸 `string | null`，而這裡要的是列舉。
 *
 * **不認識的值就當成沒有** —— 後端只會存白名單內的值，所以一個表外的值
 * 只可能來自舊資料或手改的 payload。兩邊都丟掉它是安全的。
 */
function asFlow(value: string | null | undefined): FlowValue | null {
  return FLOW_OPTIONS.includes(value as FlowValue) ? (value as FlowValue) : null;
}

function asCramp(value: string | null | undefined): CrampValue | null {
  return CRAMP_OPTIONS.includes(value as CrampValue)
    ? (value as CrampValue)
    : null;
}

function ChoiceRow({
  label,
  options,
  value,
  onChange,
  testIdPrefix,
  petals,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string | null;
  onChange: (value: string | null) => void;
  testIdPrefix: string;
  /** 每個選項畫幾瓣花 —— 流量大小的視覺語言。 */
  petals?: number;
}) {
  return (
    <div className="flex flex-col gap-s-1.5">
      <span className="font-label text-ink-2">{label}</span>
      <div className="flex flex-wrap gap-s-1" role="group" aria-label={label}>
        {options.map((o, i) => {
          const on = value === o.value;
          const count = petals ? Math.min(petals, i + 1) : 0;
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(on ? null : o.value)}
              data-testid={`${testIdPrefix}-${o.value}`}
              className={cn(
                "min-h-11 rounded-pill border px-s-3 text-body-sm",
                on
                  ? "border-ink-1 bg-ink-1 text-paper"
                  : "border-hairline-strong text-ink-2 hover:bg-black/5",
              )}
            >
              {/* **花瓣數量就是流量大小。** 點滴 1 瓣、少 2 瓣、中 3 瓣、多 4 瓣 ——
                  「以花瓣數量呈現」是使用者指定的語言，而這裡本來就有一朵花，
                  所以數量是最自然的編碼。

                  **花瓣是「數量」不是「大小」**：四顆一樣大的點比一顆大一倍的點
                  更容易比較，而這四個值要比較的正是「有幾瓣」。 */}
              {count > 0 ? (
                <span className="mr-s-1 inline-flex items-center gap-0.5 align-middle">
                  {Array.from({ length: count }).map((_, k) => (
                    <span
                      key={k}
                      className={cn(
                        "h-[5px] w-[5px] rounded-pill",
                        on ? "bg-paper" : "bg-ink-3",
                      )}
                    />
                  ))}
                </span>
              ) : null}
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
